import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { ReactElement } from 'react';

import { formatReported } from '~/utils/date/date';
import { ICeArchivedEntry, ICeEntry } from '~/services/api/communityEngagement/types';
import { ReactComponent as DocumentListIcon } from '~/ui/assets/images/documentList.svg';

import ArchivedRow from '../ArchivedRow';
import ArchivedSection from '../ArchivedSection';
import DocumentList from '../DocumentList';
import EntryAmount from '../EntryAmount';
import RowActions from '../RowActions';
import ceStyles from '~/ui/pages/Clients/reusable/CommunityEngagement/CommunityEngagement.module.scss';

import styles from './EntriesList.module.scss';

interface IEntriesListProps {
  entries: ICeEntry[];
  archivedEntries: ICeArchivedEntry[];
  showEmptyState: boolean;
  onEditEntry: (entry: ICeEntry) => void;
  onArchiveEntry: (entry: ICeEntry) => void;
  onRestoreEntry: (entry: ICeArchivedEntry) => void;
  isRestoring: boolean;
}

const EntriesList = ({
  entries,
  archivedEntries,
  showEmptyState,
  onEditEntry,
  onArchiveEntry,
  onRestoreEntry,
  isRestoring,
}: IEntriesListProps): ReactElement => (
  <>
    {showEmptyState && (
      <div className={styles.emptyState}>
        <DocumentListIcon className={styles.emptyIcon} />
        <p className={styles.emptyText}>No entries for this month</p>
      </div>
    )}
    {(entries.length > 0 || archivedEntries.length > 0) && (
      <div className={ceStyles.card}>
        {entries.length > 0 && (
          <div className={ceStyles.cardHeader}>
            <h4 className={ceStyles.cardTitle}>Entries</h4>
          </div>
        )}
        {entries.map(entry => (
          <div key={entry.id} className={ceStyles.row}>
            <div className={ceStyles.rowSummary}>
              <EntryAmount
                minutesWorked={entry.minutesWorked}
                currencyEarned={entry.currencyEarned}
              />
              <div className={ceStyles.rowSubtitle}>{formatReported(entry)}</div>
            </div>
            <div className={ceStyles.rowDocuments}>
              {entry.documents.length ? (
                <DocumentList documents={entry.documents} />
              ) : (
                <div className={styles.missingDocument}>
                  <WarningAmberRounded fontSize="small" className={styles.warningIcon} />
                  Upload a supporting document
                </div>
              )}
            </div>
            <RowActions
              subject="entry"
              onEdit={() => onEditEntry(entry)}
              onArchive={() => onArchiveEntry(entry)}
            />
          </div>
        ))}
        {archivedEntries.length > 0 && (
          <ArchivedSection>
            {archivedEntries.map(entry => (
              <ArchivedRow
                key={entry.id}
                title={
                  <EntryAmount
                    minutesWorked={entry.minutesWorked}
                    currencyEarned={entry.currencyEarned}
                  />
                }
                subtitle={formatReported(entry)}
                onRestore={() => onRestoreEntry(entry)}
                isRestoreDisabled={isRestoring}
              />
            ))}
          </ArchivedSection>
        )}
      </div>
    )}
  </>
);

export default EntriesList;
